import { useEffect, useState, type ReactNode } from 'react';
import { ArrowUpRight, Check, ChevronDown, CircleHelp, ThumbsDown, ThumbsUp, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { AutonomySwitch } from '../components/policy/AutonomySwitch';
import { api, formatMoney, send, type AwayFeed, type CashForecastView, type HomeBriefing, type HomeView, type ManagerNote, type NoteSettings, type Session } from '../api';
import { formatLongDay, formatMoment, useDeskData } from '../components/desk';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { JobProgress } from '../amy/JobProgress';
import { SetupChecklist } from '../components/SetupChecklist';
import './home.css';
import { BlockedChips, firstRowPerCustomer } from '../components/blocked-chip';
import { PresenceLine, usePresence } from '../components/presence';

/**
 * The agent's home, the landing page, laid out like walking up to her desk (#281): what she is doing now and next, her
 * briefing with the names in it linked, what needs a person, then the numbers and what happened since you were away. Her
 * commitments and note sit on the right. Decisions happen in Collections; every one goes through the approvals route.
 */
type Doing = NonNullable<HomeView['autonomy']>['emails'][number];
const moneyText = (m: { minor: string; currency: string } | null) => m ? formatMoney(m.minor, m.currency) : null;
const idem = () => crypto.randomUUID();

function useBriefing(view: HomeView | null) {
  const [briefing, setBriefing] = useState<HomeBriefing | null>(null);
  const key = view?.briefingKey;
  useEffect(() => {
    if (!view) return;
    if (view.briefing) { setBriefing(view.briefing); return; }
    let active = true;
    void api<HomeBriefing>('/app-api/home/briefing').then(b => { if (active) setBriefing(b); }).catch(() => {});
    return () => { active = false; };
    // A new fact set (a new key) is the only reason to write again.
  }, [key]);
  return briefing;
}

interface CarolOps {
  enabled: boolean; paused: boolean; waiting: number; oldestWaitSeconds: number | null; scheduled: number; failedToday: number;
  stops: { id: string; customerId: string; customerName: string; label: string; at: string; error: string | null }[];
}
const waitText = (seconds: number) => seconds < 90 ? `${seconds}s` : seconds < 5400 ? `${Math.round(seconds / 60)} min` : `${Math.round(seconds / 3600)} h`;

/** The ops line for Carol's event worker (#328): shown only where CAROL_AGENT is on. */
function CarolOpsLine({ persona }: { persona: string }) {
  const { data } = useDeskData<CarolOps>('/app-api/home/carol');
  if (!data?.enabled) return null;
  const parts = [
    data.paused ? `${persona} is paused: events are recorded, none are worked` : null,
    `${data.waiting} event${data.waiting === 1 ? '' : 's'} waiting`,
    data.oldestWaitSeconds !== null && data.waiting > 0 ? `oldest ${waitText(data.oldestWaitSeconds)}` : null,
    `${data.failedToday} failed today`,
  ].filter(Boolean);
  return <div className="home-carol-ops" role="status" aria-label={`${persona}'s event queue`}>
    <p className="home-carol-ops-line">{parts.join(' · ')}</p>
    {data.stops.length > 0 && <ul className="home-carol-stops" aria-label={`${persona}'s stops today`}>
      {data.stops.slice(0, 5).map(s => <li key={s.id}>
        <a href={`#customers/${encodeURIComponent(s.customerId)}`}>{s.customerName}</a>: {s.label}, stopped{s.error ? ` (${s.error})` : ''}
      </li>)}
    </ul>}
  </div>;
}

export function Home({ session, persona }: { session: Session; persona: string }) {
  const { data: view, error, reload } = useDeskData<HomeView>('/app-api/home');
  const briefing = useBriefing(view);
  const canAct = session.actor.roles.some(r => r === 'owner' || r === 'approver');
  const owner = session.actor.roles.includes('owner');
  const mode = view?.mode ?? 'approve';
  const { presence, now, reload: reloadPresence } = usePresence();
  return <main id="main-content" className="home-page">
    <header className="home-opening">
      <div className="home-opening-text">
        <div className="home-dateline">
          <span className="home-date">{view ? formatLongDay(view.asOf) : <Skeleton className="h-3 w-40" />}</span>
          {view?.shadow && <span className="home-shadow-pill" role="note" title="Shadow mode: approvals are recorded and nothing is emailed to customers."
            aria-label="Shadow mode: approvals are recorded and nothing is emailed to customers.">Shadow · nothing is emailed</span>}
        </div>
        <h1 className="home-greeting">{view ? view.greeting : 'Hello'}<span className="heading-dot" aria-hidden>.</span></h1>
        <PresenceLine presence={presence} now={now} canAct={canAct} onChange={() => { reloadPresence(); reload(); }} />
        <CarolOpsLine persona={persona} />
        <div className="home-note" aria-live="polite" aria-busy={!briefing}>
          {briefing ? <div className="home-note-text">
            {briefing.lines.slice(0, 3).map((line, i) => <p key={i}><Linked text={line} links={view?.links ?? []} /></p>)}</div>
            : <div className="home-note-loading" role="status" aria-label={`${persona} is writing your briefing`}>
              <Skeleton className="h-4 w-[90%]" /><Skeleton className="h-4 w-[60%]" /></div>}
        </div>
      </div>
    </header>
    {error && <div className="alert error" role="alert">{error}</div>}
    <div className="home-grid">
      <div className="home-main">
        {view && <div className="home-mode"><AutonomySwitch compact mode={mode} owner={owner} /></div>}
        <NeedsYouCard view={view} persona={persona} />
        {view && mode !== 'approve' && <AmyDoingCard view={view} canAct={canAct} persona={persona} onChange={reload} />}
        <HomeTiles view={view} />
        <AwaySection view={view} canAct={canAct} persona={persona} />
        {view && mode !== 'approve' && canAct && <SpotCheckCard persona={persona} />}
        {/* Idle is already on the status line, so the section shows only when there is work in it. */}
        {view && (view.working.jobs.length > 0 || view.working.next.length > 0) && <section className="home-section" aria-labelledby="home-working">
          <h2 id="home-working" className="home-section-title">What I’m working on</h2>
          {view.working.jobs.map(job => <JobProgress key={job.id} initial={job} canAct={canAct} defaultOpen={false} />)}
          {view.working.next.length > 0 && <div className="home-next">
            <h3>Next drafts I’ll pick up</h3>
            <ol>{view.working.next.map(n => <li key={`${n.customerId}:${n.why}`}>
              <a href={`#customers/${encodeURIComponent(n.customerId)}`}>{n.customerName}</a><span>{n.why}</span></li>)}</ol>
          </div>}
        </section>}
        <SetupChecklist owner={owner} />
      </div>
      <div className="home-side">
        <OwesSection view={view} persona={persona} />
        <ManagerNoteCard persona={persona} owner={owner} />
        <section className="home-section" aria-labelledby="home-watching">
          <h2 id="home-watching" className="home-section-title home-watch-title">Watching
            <Tooltip><TooltipTrigger asChild><button type="button" className="home-help" aria-label="How this list is ordered">
              <CircleHelp size={14} aria-hidden /></button></TooltipTrigger>
              <TooltipContent className="max-w-72">Ordered by the cash I expect to recover: what is overdue, times how likely it is to come in
                for that verdict (pay soon 80%, worth a look 60%, needs a call 40%, escalate 25%, uncollectible 5%).</TooltipContent></Tooltip></h2>
          {!view ? <ListSkeleton /> : view.watching.length ? <ol className="home-watch">{view.watching.map(c => <li key={c.id}>
            <a href={`#customers/${encodeURIComponent(c.id)}`}>
              <span className={`home-risk ${c.level}`} aria-hidden />
              <span className="home-watch-body"><strong>{c.name}</strong>
                <span className={`home-verdict v-${c.verdict}`}>{c.verdictText}</span>
                <small>{c.overdue.map(m => formatMoney(m.minor, m.currency)).join(' and ')} overdue, oldest {c.oldestDaysOverdue} days. {c.fact}.</small></span>
              <ArrowUpRight size={14} aria-hidden className="home-watch-go" /></a></li>)}</ol>
            : <p className="home-empty">Nobody is overdue. I’ll flag anyone who starts to slip.</p>}
        </section>
      </div>
    </div>
  </main>;
}

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Money in a sentence: "$2,300.00", "$2.9M", "EUR 1,200". */
const money = /(?:[$€£¥]\s?\d[\d,]*(?:\.\d+)?(?:[KMB]\b)?|\b[A-Z]{3}\s\d[\d,]*(?:\.\d+)?)/;
/** A briefing line with each customer it names linked to where it opens, and amounts set as quiet chips. */
function Linked({ text, links }: { text: string; links: HomeView['links'] }) {
  const names = [...links].sort((a, b) => b.text.length - a.text.length);
  const pattern = new RegExp(`(${[...names.map(l => escape(l.text)), money.source].join('|')})`, 'g');
  const href = new Map(names.map(l => [l.text, l.href]));
  return <>{text.split(pattern).map((part, i) => {
    if (i % 2 === 0) return part;
    const to = href.get(part);
    return to ? <a key={i} href={to} className="home-note-link">{part}</a> : <span key={i} className="home-note-money">{part}</span>;
  })}</>;
}

/** "Carol owes: 3" (#215): what she and the team promised customers and have not done yet, earliest due first. */
function OwesSection({ view, persona }: { view: HomeView | null; persona: string }) {
  const owes = view?.owes;
  // Nothing owed is not news: the section appears when the agent or the team has promised something.
  if (owes && !owes.items.length && !owes.team.open) return null;
  return <section className="home-section" aria-labelledby="home-owes">
    <h2 id="home-owes" className="home-section-title">{persona} owes: {owes ? owes.carol.open : '…'}
      {owes && owes.team.open > 0 && <small className="home-owes-team"> · the team owes {owes.team.open}</small>}</h2>
    {!owes ? <ListSkeleton /> : owes.items.length ? <ol className="home-owes">{owes.items.map(c => <li key={c.id}>
      <a href={`#customers/${encodeURIComponent(c.customerId)}`}><strong>{c.customerName}</strong></a>
      <p>{c.text}</p>
      <small className={c.overdue ? 'home-owes-late' : undefined}>
        {c.owner === 'carol' ? persona : 'The team'} · {c.overdue ? 'past due' : `due ${formatMoment(c.dueAt, view!.asOf)}`}
        {c.needsYouTaskId ? ' · on Needs you' : ''}</small>
    </li>)}</ol> : <p className="home-empty">Nothing promised and not yet done.</p>}
  </section>;
}

/**
 * Amounts at a glance: "$2.91M", exact on hover. Computed on bigint minor units, so no figure passes through a float.
 */
export function compactMoney(minor: string, currency: string): string {
  const exact = formatMoney(minor, currency);
  let value = BigInt(minor); const negative = value < 0n; if (negative) value = -value;
  const exponent = new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  const units = value / 10n ** BigInt(exponent);
  const steps: [bigint, string][] = [[1_000_000_000n, 'B'], [1_000_000n, 'M'], [1_000n, 'K']];
  for (const [size, suffix] of steps) {
    if (units < size) continue;
    const hundredths = (units * 100n + size / 2n) / size;
    const whole = hundredths / 100n; const frac = (hundredths % 100n).toString().padStart(2, '0').replace(/0+$/, '');
    const symbol = exact.replace(/[\d.,\s-]/g, '') || `${currency} `;
    return `${negative ? '-' : ''}${symbol}${whole}${frac ? `.${frac}` : ''}${suffix}`;
  }
  return exact.replace(/\.00$/, '');
}
const moneyTitle = (list: { minor: string; currency: string }[]) => list.map(m => formatMoney(m.minor, m.currency)).join(' + ');
const moneyCompact = (list: { minor: string; currency: string }[]) => list.map(m => compactMoney(m.minor, m.currency)).join(' + ');
const counted = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** One currency's change against last week, when both weeks are in the same single currency. */
function weekChange(now: { minor: string; currency: string }[], before: { minor: string; currency: string }[]) {
  if (now.length > 1 || before.length > 1) return null;
  const currency = now[0]?.currency ?? before[0]?.currency;
  if (!currency || (now[0] && before[0] && now[0].currency !== before[0].currency)) return null;
  const delta = BigInt(now[0]?.minor ?? '0') - BigInt(before[0]?.minor ?? '0');
  if (delta === 0n) return { text: 'Same as last week', tone: 'flat' };
  const size = compactMoney((delta < 0n ? -delta : delta).toString(), currency);
  return { text: `${delta > 0n ? '↑' : '↓'} ${size} vs last week`, tone: delta > 0n ? 'up' : 'down' };
}

/**
 * Four numbers that mean something to a collector: cash in, cash promised, cash at risk and what waits on you. Each opens
 * where it is worked. An empty value is quiet; a real one gets the space.
 */
function HomeTiles({ view }: { view: HomeView | null }) {
  const { data: cash } = useDeskData<CashForecastView>('/app-api/cash/forecast');
  const week = cash?.windows.thisWeek; const likely = cash?.likely.thisWeek;
  const tile = (label: string, href: string, value: ReactNode | null, sub: ReactNode) =>
    <a className="home-tile" href={href}>
      <span className="home-tile-label">{label}</span>
      {value === null ? <Skeleton className="h-6 w-24" /> : value}
      <small>{sub}</small>
    </a>;
  const amount = (list: { minor: string; currency: string }[], empty: string) => list.length
    ? <strong title={moneyTitle(list)}>{moneyCompact(list)}</strong> : <strong className="empty">{empty}</strong>;
  const change = view ? weekChange(view.collectedThisWeek, view.collectedLastWeek) : null;
  const risk = view?.atRisk;
  return <section className="home-tiles" aria-label="This week at a glance">
    {tile('Collected', '#cash', view ? amount(view.collectedThisWeek, '$0') : null,
      view ? <>Since Monday{change && <span className={`home-tile-change ${change.tone}`}>{change.text}</span>}</> : ' ')}
    {tile('Promised', '#cash', week ? amount(week.promised, 'None') : null,
      week ? `${counted(week.promisedCount, 'promise', 'promises')} due this week${likely?.amounts.length ? ` · likely ${moneyCompact(likely.amounts)}` : ''}` : ' ')}
    {tile('At risk', '#customers', risk ? (risk.amount ? <strong title={formatMoney(risk.amount.minor, risk.amount.currency)}>
      {compactMoney(risk.amount.minor, risk.amount.currency)}</strong> : <strong className="empty">{risk.customers ? 'Mixed currencies' : 'Nothing'}</strong>) : null,
      risk ? risk.customers ? `Overdue at ${counted(risk.customers, 'account', 'accounts')} that need attention` : 'No account needs attention' : ' ')}
  </section>;
}

const actorWord = (actor: AwayFeed['items'][number]['actor'], persona: string) =>
  actor === 'agent' ? persona : actor === 'customer' ? 'Customer' : 'Your team';

/**
 * What happened since you were away (the last day), newest first, each line saying who acted and opening on the customer.
 * "Why?" shows the record's own reasons: the decision's rationale, what the customer said, how a reply was read. Anything
 * still in its Undo window is listed first with its Undo.
 */
function AwaySection({ view, canAct, persona }: { view: HomeView | null; canAct: boolean; persona: string }) {
  const { data: feed } = useDeskData<AwayFeed>('/app-api/home/away');
  const [open, setOpen] = useState<string | null>(null);
  const undoable = (view?.did ?? []).filter(d => d.undo);
  return <section className="home-section" aria-labelledby="home-away">
    <h2 id="home-away" className="home-section-title">Since you were away <small className="home-section-sub">the last 24 hours</small></h2>
    {canAct && undoable.length > 0 && <ol className="home-did home-undoable" aria-label="You can still undo">{undoable.map(item => <li key={item.id}>
      <time dateTime={item.at}>{formatMoment(item.at, view!.asOf)}</time>
      <p>{item.summary}</p>
      <UndoButton undo={item.undo!} />
    </li>)}</ol>}
    {!feed ? <ListSkeleton /> : feed.items.length ? <ol className="home-away">{feed.items.map(item => {
      const expanded = open === item.id;
      return <li key={item.id} className={`actor-${item.actor}`}>
        <time dateTime={item.at}>{formatMoment(item.at, view?.asOf)}</time>
        <div className="home-away-body">
          <p><span className="home-away-who">{actorWord(item.actor, persona)}</span>
            {item.href ? <a href={item.href}>{item.title}</a> : item.title}
            {item.amount && <span className="home-note-money">{formatMoney(item.amount.minor, item.amount.currency)}</span>}</p>
          {item.customerId && <a className="home-away-customer" href={`#customers/${encodeURIComponent(item.customerId)}`}>{item.customerName}</a>}
          {item.why && <div className="home-away-why" id={`why-${item.id}`} hidden={!expanded}>
            {item.why.detail && <p>{item.why.detail}</p>}
            {item.why.notes.length > 0 && <ul>{item.why.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
            {item.customerId && <a href={`#customers/${encodeURIComponent(item.customerId)}`}>Open the timeline<ArrowUpRight size={12} aria-hidden /></a>}
          </div>}
        </div>
        {item.why && <button type="button" className="home-away-toggle" aria-expanded={expanded} aria-controls={`why-${item.id}`}
          onClick={() => setOpen(expanded ? null : item.id)}>Why?<ChevronDown size={12} aria-hidden /></button>}
      </li>;
    })}</ol> : <p className="home-empty">Nothing happened in the last day. Sends, replies, calls and payments show up here as they happen.</p>}
  </section>;
}

/**
 * What waits on a person, as a doorway to Collections (2026-10-31): the counts and the top three, each opening its case
 * there. Decisions happen in Collections, beside the draft and the account's story, not in a second list here.
 */
function NeedsYouCard({ view, persona }: { view: HomeView | null; persona: string }) {
  const mode = view?.mode ?? 'approve';
  const total = view?.needsYou.total ?? 0;
  const rows = (view?.needsYou.top ?? []).slice(0, 3);
  const chipsOn = firstRowPerCustomer(rows.map(row => ({ key: row.key, customerId: row.customerName })));
  const title = mode === 'autonomous' ? 'Needs a person' : 'Needs you';
  const counts = view?.needsYou.counts;
  const chips = counts ? [
    { label: mode === 'autonomous' ? 'Held drafts' : 'Drafts', n: counts.approvals, href: '#collections?status=drafted' },
    { label: 'Replies', n: counts.replies, href: '#collections?status=needs_you' },
    { label: 'Tasks', n: counts.tasks, href: '#collections?status=needs_you' },
  ].filter(c => c.n > 0) : [];
  return <section className="home-needs" aria-labelledby="home-needs-title">
    <header>
      <div className="home-needs-heading"><h2 id="home-needs-title" className="home-needs-title">{title}</h2>
        {view && total > 0 && <span className="home-count" aria-label={`${total} waiting`}>{total}</span>}</div>
      <div className="home-needs-head-actions">
        <Button size="sm" asChild><a className="home-review-all" href="#collections">Open Collections<ArrowUpRight aria-hidden /></a></Button>
      </div>
    </header>
    {chips.length > 1 && <nav className="home-needs-chips" aria-label="What is waiting">{chips.map(c =>
      <a key={c.label} href={c.href}>{c.label}<span>{c.n}</span></a>)}</nav>}
    {!view ? <ListSkeleton /> : total === 0 ? <p className="home-empty home-all-clear"><Check size={15} aria-hidden />{mode === 'autonomous'
      ? `${persona} sends routine emails herself. Replies, disputes and anything unusual land in Collections.`
      : `${persona} will put drafts, replies and tasks in Collections when they need a person.`}</p>
      : <ol className="home-needs-list">{rows.map(item => <li key={item.key} className="home-need">
        <a className="home-need-main" href={`#collections/${encodeURIComponent(item.key)}`}>
          <span className="home-needs-who"><strong>{item.customerName}</strong>
            {item.amount && <span>{moneyText(item.amount)}</span>}</span>
          <span className="home-needs-want">{item.want}</span>
          <span className="home-needs-why">{item.brief}</span>
        </a>
        {chipsOn.has(item.key) && item.blockers.length > 0 && <BlockedChips blockers={item.blockers} label={`Why ${item.customerName} is held`} />}
      </li>)}</ol>}
  </section>;
}

/** Autonomous and mixed instances: what Amy is sending herself. Hold stops one (Undo in its window, or before it sends). */
function AmyDoingCard({ view, canAct, persona, onChange }: { view: HomeView; canAct: boolean; persona: string; onChange: () => void }) {
  const a = view.autonomy!;
  const [busy, setBusy] = useState(false);
  const sentWord = view.shadow ? 'would have sent' : 'sent';
  async function hold(email: Doing) {
    setBusy(true);
    try {
      await api(`/app-api/approvals/${email.approvalId}/decisions`, { kind: email.hold!, revision: email.revision, idempotencyKey: idem() });
      toast.success(`Held: the email to ${email.customerName} will not go out.`); onChange();
    } catch (error) { toast.error(error instanceof Error ? error.message : 'It could not be held; it may already be sending.'); onChange(); }
    finally { setBusy(false); }
  }
  const when = (email: Doing) => email.state === 'sent' ? `${view.shadow ? 'Would have sent' : 'Sent'} ${email.at ? formatMoment(email.at, view.asOf) : ''}`
    : email.state === 'holdable' ? `Sends ${email.at ? formatMoment(email.at, view.asOf) : 'shortly'}` : 'Waiting for the send window';
  return <section className="home-needs home-doing" aria-labelledby="home-doing-title">
    <header>
      <div><h2 id="home-doing-title" className="home-needs-title">What {persona} is doing</h2>
        <p className="home-doing-count">{a.sentToday} {sentWord} today · {a.scheduled} lined up{a.paused ? ' · paused' : ''}</p></div>
    </header>
    {!a.emails.length ? <p className="home-empty">No routine emails are due from {persona} today.</p>
      : <ol className="home-needs-list">{a.emails.slice(0, 8).map(email => <li key={email.approvalId} className="home-need">
        <a className="home-need-main" href={`#approvals/${encodeURIComponent(email.approvalId)}`}>
          <span className="home-needs-who"><strong>{email.customerName}</strong>{email.amount && <span>{moneyText(email.amount)}</span>}</span>
          <span className="home-needs-want">{email.subject}</span>
          <span className="home-needs-why">{when(email)}</span>
        </a>
        <div className="home-need-actions">
          {canAct && email.hold && <Button size="sm" variant="outline" disabled={busy} onClick={() => void hold(email)}><Undo2 aria-hidden />Hold</Button>}
          <Button size="sm" variant="ghost" asChild><a href={`#approvals/${encodeURIComponent(email.approvalId)}`}>Open</a></Button>
        </div>
      </li>)}</ol>}
  </section>;
}

/** A small daily sample of emails Amy approved herself, for a quick "is this like us?" check. Feedback only. */
function SpotCheckCard({ persona }: { persona: string }) {
  const { data, reload } = useDeskData<{ items: { approvalId: string; customerName: string; subject: string; body: string; sentAt: string }[] }>('/app-api/home/spot-checks');
  const [open, setOpen] = useState<string | null>(null);
  if (!data?.items.length) return null;
  async function mark(id: string, verdict: 'looks_right' | 'not_like_us') {
    try { await api('/app-api/home/spot-checks', { approvalId: id, verdict }); toast.success(verdict === 'looks_right' ? 'Thanks, noted.' : `Noted. ${persona} will learn from this.`); reload(); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Please try again.'); }
  }
  return <section className="home-section home-spot" aria-labelledby="home-spot-title">
    <h2 id="home-spot-title" className="home-section-title">Spot-check {data.items.length}</h2>
    <p className="home-spot-lead">A few emails {persona} sent without waiting for you. Do they sound like you?</p>
    <ol className="home-spot-list">{data.items.map(item => <li key={item.approvalId}>
      <button type="button" className="home-spot-head" aria-expanded={open === item.approvalId}
        onClick={() => setOpen(open === item.approvalId ? null : item.approvalId)}>
        <strong>{item.customerName}</strong><span>{item.subject}</span></button>
      {open === item.approvalId && <div className="home-spot-body">{item.body.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}</div>}
      <div className="home-need-actions">
        <Button size="sm" variant="outline" onClick={() => void mark(item.approvalId, 'looks_right')}><ThumbsUp aria-hidden />Looks right</Button>
        <Button size="sm" variant="ghost" onClick={() => void mark(item.approvalId, 'not_like_us')}><ThumbsDown aria-hidden />Not like us</Button>
      </div>
    </li>)}</ol>
  </section>;
}

/**
 * The note to the manager (#217): today's note or last week's summary. The narrative is the agent's words, checked against
 * the ledger; the numbers and recommendations are computed. Owners choose what posts to Slack and which roles see it here.
 */
function ManagerNoteCard({ persona, owner }: { persona: string; owner: boolean }) {
  const [kind, setKind] = useState<'daily' | 'weekly'>('daily');
  // Closed by default: the briefing above says the day in brief; the full note is a click away.
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<ManagerNote | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const { data: settings, reload } = useDeskData<NoteSettings>('/app-api/settings/manager-note');
  const visible = settings?.visible ?? false;
  useEffect(() => {
    if (!visible || !open) return;
    let active = true;
    setNote(null); setFailed(null);
    void api<ManagerNote>(`/app-api/home/note?kind=${kind}`).then(n => { if (active) setNote(n); })
      .catch((error: unknown) => { if (active) setFailed(error instanceof Error ? error.message : 'The note could not be loaded.'); });
    return () => { active = false; };
  }, [kind, visible, open]);
  if (!settings || !visible) return null;
  async function change(next: Partial<NoteSettings>) {
    const current = { daily: settings!.daily, weekly: settings!.weekly, homeRoles: settings!.homeRoles };
    try { await send('/app-api/settings/manager-note', 'PUT', { ...current, ...next }); reload(); toast.success('Saved.'); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Please try again.'); }
  }
  const shown = (role: 'approver' | 'viewer') => settings.homeRoles.includes(role);
  const toggleRole = (role: 'approver' | 'viewer') => void change({ homeRoles: shown(role)
    ? settings.homeRoles.filter(r => r !== role) : [...settings.homeRoles, role] });
  return <section className="home-section home-report" aria-labelledby="home-report-title">
    <h2 id="home-report-title" className="home-section-title">
      <button type="button" className="home-report-toggle" aria-expanded={open} aria-controls="home-report-panel" onClick={() => setOpen(!open)}>
        {persona}’s note<ChevronDown size={14} aria-hidden /></button></h2>
    <div id="home-report-panel" hidden={!open}>
    <div className="home-report-tabs" role="group" aria-label="Which note">
      <Button size="sm" variant={kind === 'daily' ? 'secondary' : 'ghost'} aria-pressed={kind === 'daily'} onClick={() => setKind('daily')}>Today</Button>
      <Button size="sm" variant={kind === 'weekly' ? 'secondary' : 'ghost'} aria-pressed={kind === 'weekly'} onClick={() => setKind('weekly')}>Last week</Button>
    </div>
    {failed ? <p className="home-empty">{failed}</p> : !note ? <ListSkeleton /> : <div className="home-report-body" aria-live="polite">
      <p className="home-report-text">{note.narrative.join(' ')}</p>
      <h3>The numbers</h3>
      <ul>{note.figures.map(line => <li key={line}>{line}</li>)}</ul>
      <h3>What I recommend</h3>
      <ul>{note.recommendations.map(line => <li key={line}>{line}</li>)}</ul>
    </div>}
    {owner && <details className="home-report-settings">
      <summary>Who gets this note</summary>
      <label><input type="checkbox" checked={settings.daily} onChange={e => void change({ daily: e.target.checked })} /> Post the daily note to Slack (after 5pm)</label>
      <label><input type="checkbox" checked={settings.weekly} onChange={e => void change({ weekly: e.target.checked })} /> Post the weekly summary to Slack (Monday)</label>
      <label><input type="checkbox" checked={shown('approver')} onChange={() => toggleRole('approver')} /> Approvers see it on Home</label>
      <label><input type="checkbox" checked={shown('viewer')} onChange={() => toggleRole('viewer')} /> Viewers see it on Home</label>
    </details>}
    </div>
  </section>;
}

function UndoButton({ undo }: { undo: NonNullable<HomeView['did'][number]['undo']> }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      if (undo.kind === 'approval') await api(`/app-api/approvals/${undo.id}/decisions`, { kind: 'undo', revision: undo.revision, idempotencyKey: idem() });
      else await api(`/app-api/amy/jobs/${undo.jobId}/steps/${undo.stepId}/undo`, {});
      toast.success(undo.kind === 'approval' ? 'Undone: the email will not go out.' : 'Undone: the promise is cancelled.');
    } catch (error) { toast.error(error instanceof Error ? error.message : 'This could not be undone.'); }
    finally { setBusy(false); }
  };
  return <Button variant="outline" size="sm" className="home-undo" disabled={busy} onClick={() => void run()}><Undo2 aria-hidden />Undo</Button>;
}

function ListSkeleton() {
  return <div className="home-skeleton" aria-hidden><Skeleton className="h-4 w-4/5" /><Skeleton className="h-4 w-3/5" /><Skeleton className="h-4 w-2/3" /></div>;
}
